import { type Mutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import { useAuthenticatedQuery } from "../lib/api";
import { processFileResponse } from "../lib/file";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

/**
 * The path of `getAllFeeStructures`. Also the FIRST element of every
 * react-query key the page walk in `useQueryFeeStructures` uses, so
 * `[FEE_STRUCTURES_PATH]` reaches every page.
 */
export const FEE_STRUCTURES_PATH = "/v1/billing/fee-structures";

/**
 * The mutation key of the fee-structure delete in `FeeStructureTable.tsx`.
 * `useQueryFeeStructures` walks the list again when a mutation with this key
 * succeeds.
 */
export const DELETE_FEE_STRUCTURES_MUTATION_KEY = [
  "DELETE",
  FEE_STRUCTURES_PATH,
] as const;

/** One item of `GET /v1/billing/fee-structures`, as the route serialises it. */
export type SerializedFeeStructureListItem = SerializedObject<
  UnpackResponse<BillingController["getAllFeeStructures"]>
>["data"][number];

const isFeeStructureDelete = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const [method, path] = mutation.options.mutationKey ?? [];
  return (
    method === DELETE_FEE_STRUCTURES_MUTATION_KEY[0] &&
    path === DELETE_FEE_STRUCTURES_MUTATION_KEY[1]
  );
};

/**
 * Every fee structure of the firm. `GET /v1/billing/fee-structures` answers a
 * page (APP-1651), and every screen that reads this hook shows, picks from or
 * looks up the whole list (`FeeStructureTable.tsx`, `BillingSummary.tsx`,
 * `CreateBillingReport.tsx`, `AccountBillingInfo.tsx`,
 * `HouseholdCashBilling.tsx`, `HouseholdCashBillingRebalance.tsx`,
 * `FirmInfo.tsx`), so this walks every page (`useAuthenticatedFetchAllPages`)
 * and holds `data` back until the last one is in.
 *
 * When the delete of `FeeStructureTable.tsx`
 * (`DELETE_FEE_STRUCTURES_MUTATION_KEY`) settles, the cached pages are dropped
 * and the walk starts again, so a mounted list shows the delete. When the last
 * reader of the list unmounts, the cached pages are dropped too: a reader
 * mounted later walks the list again rather than collecting pages cached
 * before a create or update (`FeeStructureInfo.tsx` sends those through
 * `useAuthenticatedMutationAsync`, which no mutation cache records). Before
 * APP-1651 a remount refetched the list for the same reason. Both follow
 * `useQueryHouseholds` (`clients/household/lib.ts`).
 */
export function useQueryFeeStructures() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedFeeStructureListItem>(
      FEE_STRUCTURES_PATH,
    );

  // Restart the walk when the delete settles. Subscribed to the mutation
  // cache's events rather than read from `useIsMutating`: a write that starts
  // and settles within one render batch never shows a pending count. The
  // delete does not throw when a row fails, so a partial failure settles as a
  // success and the list is read again then too.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isFeeStructureDelete(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [FEE_STRUCTURES_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [FEE_STRUCTURES_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedFeeStructureListItem[] } | undefined = useMemo(
    () => (hasEveryRow ? { data: rows } : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  // Sends the walk again from offset 0, also when its first page failed: the
  // failed page's query is invalidated, as `reload` alone leaves it as it is.
  const refetch = useCallback(() => {
    reload();
    void queryClient.invalidateQueries({ queryKey: [FEE_STRUCTURES_PATH] });
  }, [queryClient, reload]);

  return { isPending, isLoading: isPending, isError, data, refetch };
}

/**
 * The path of `getAllBillingSplits`. Also the FIRST element of every
 * react-query key the page walk in `useQueryBillingSplits` uses, so
 * `[BILLING_SPLITS_PATH]` reaches every page.
 */
export const BILLING_SPLITS_PATH = "/v1/billing/billing-splits";

/**
 * The mutation key of the billing-split delete in `BillingSplitsTable.tsx`.
 * `useQueryBillingSplits` walks the list again when a mutation with this key
 * succeeds.
 */
export const DELETE_BILLING_SPLITS_MUTATION_KEY = [
  "DELETE",
  BILLING_SPLITS_PATH,
] as const;

/** One item of `GET /v1/billing/billing-splits`, as the route serialises it. */
export type SerializedBillingSplitListItem = SerializedObject<
  UnpackResponse<BillingController["getAllBillingSplits"]>
>["data"][number];

const isBillingSplitDelete = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const [method, path] = mutation.options.mutationKey ?? [];
  return (
    method === DELETE_BILLING_SPLITS_MUTATION_KEY[0] &&
    path === DELETE_BILLING_SPLITS_MUTATION_KEY[1]
  );
};

/**
 * Every billing split of the firm. `GET /v1/billing/billing-splits` answers a
 * page (APP-1651), and both screens that read this hook show or pick from the
 * whole list (`BillingSplitsTable.tsx`, `AccountBillingInfo.tsx`), so this
 * walks every page (`useAuthenticatedFetchAllPages`) and holds `data` back
 * until the last one is in.
 *
 * When the delete of `BillingSplitsTable.tsx`
 * (`DELETE_BILLING_SPLITS_MUTATION_KEY`) settles, the cached pages are dropped
 * and the walk starts again, so a mounted list shows the delete. When the last
 * reader of the list unmounts, the cached pages are dropped too: a reader
 * mounted later walks the list again rather than collecting pages cached
 * before a create or update (`BillingSplitsInfo.tsx` sends those through
 * `useAuthenticatedMutationAsync`, which no mutation cache records). Both
 * follow `useQueryFeeStructures` above.
 */
export function useQueryBillingSplits() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedBillingSplitListItem>(
      BILLING_SPLITS_PATH,
    );

  // Restart the walk when the delete settles. See `useQueryFeeStructures` for
  // why this subscribes to the mutation cache's events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isBillingSplitDelete(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [BILLING_SPLITS_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [BILLING_SPLITS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedBillingSplitListItem[] } | undefined = useMemo(
    () => (hasEveryRow ? { data: rows } : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

/**
 * The path of `getAllBillingMinimums`. Also the FIRST element of every
 * react-query key the page walk in `useQueryBillingMinimums` uses, so
 * `[BILLING_MINIMUMS_PATH]` reaches every page.
 */
export const BILLING_MINIMUMS_PATH = "/v1/billing/billing-minimums";

/**
 * The mutation key of the billing-minimum delete in `BillingMinimumTable.tsx`.
 * `useQueryBillingMinimums` walks the list again when a mutation with this key
 * succeeds.
 */
export const DELETE_BILLING_MINIMUMS_MUTATION_KEY = [
  "DELETE",
  BILLING_MINIMUMS_PATH,
] as const;

/** One item of `GET /v1/billing/billing-minimums`, as the route serialises it. */
export type SerializedBillingMinimumListItem = SerializedObject<
  UnpackResponse<BillingController["getAllBillingMinimums"]>
>["data"][number];

const isBillingMinimumDelete = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const [method, path] = mutation.options.mutationKey ?? [];
  return (
    method === DELETE_BILLING_MINIMUMS_MUTATION_KEY[0] &&
    path === DELETE_BILLING_MINIMUMS_MUTATION_KEY[1]
  );
};

/**
 * Every billing minimum of the firm. `GET /v1/billing/billing-minimums` answers
 * a page (APP-1651), and both screens that read this hook show or pick from the
 * whole list (`BillingMinimumTable.tsx`, `HouseholdCashBilling.tsx`), so this
 * walks every page (`useAuthenticatedFetchAllPages`) and holds `data` back
 * until the last one is in.
 *
 * When the delete of `BillingMinimumTable.tsx`
 * (`DELETE_BILLING_MINIMUMS_MUTATION_KEY`) settles, the cached pages are
 * dropped and the walk starts again, so a mounted list shows the delete. When
 * the last reader of the list unmounts, the cached pages are dropped too: a
 * reader mounted later walks the list again rather than collecting pages
 * cached before a create or update (`BillingMinimumInfo.tsx` sends those
 * through `useAuthenticatedMutationAsync`, which no mutation cache records).
 * Both follow `useQueryFeeStructures` above.
 */
export function useQueryBillingMinimums() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedBillingMinimumListItem>(
      BILLING_MINIMUMS_PATH,
    );

  // Restart the walk when the delete settles. See `useQueryFeeStructures` for
  // why this subscribes to the mutation cache's events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isBillingMinimumDelete(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [BILLING_MINIMUMS_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [BILLING_MINIMUMS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedBillingMinimumListItem[] } | undefined =
    useMemo(
      () => (hasEveryRow ? { data: rows } : undefined),
      [rows, hasEveryRow],
    );

  const isPending = !hasEveryRow && !isError;

  // Sends the walk again from offset 0, also when its first page failed: the
  // failed page's query is invalidated, as `reload` alone leaves it as it is.
  const refetch = useCallback(() => {
    reload();
    void queryClient.invalidateQueries({ queryKey: [BILLING_MINIMUMS_PATH] });
  }, [queryClient, reload]);

  return { isPending, isLoading: isPending, isError, data, refetch };
}

/**
 * The path of `getAllAssetExclusions`. Also the FIRST element of every
 * react-query key the page walk in `useQueryAssetExclusions` uses, so
 * `[ASSET_EXCLUSIONS_PATH]` reaches every page.
 */
export const ASSET_EXCLUSIONS_PATH = "/v1/billing/asset-exclusions";

/**
 * The mutation key of the asset-exclusion delete in `AssetExclusionTable.tsx`.
 * `useQueryAssetExclusions` walks the list again when a mutation with this key
 * succeeds.
 */
export const DELETE_ASSET_EXCLUSIONS_MUTATION_KEY = [
  "DELETE",
  ASSET_EXCLUSIONS_PATH,
] as const;

/** One item of `GET /v1/billing/asset-exclusions`, as the route serialises it. */
export type SerializedAssetExclusionListItem = SerializedObject<
  UnpackResponse<BillingController["getAllAssetExclusions"]>
>["data"][number];

const isAssetExclusionDelete = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const [method, path] = mutation.options.mutationKey ?? [];
  return (
    method === DELETE_ASSET_EXCLUSIONS_MUTATION_KEY[0] &&
    path === DELETE_ASSET_EXCLUSIONS_MUTATION_KEY[1]
  );
};

/**
 * Every asset exclusion of the firm. `GET /v1/billing/asset-exclusions`
 * answers a page (APP-1651), and both screens that read this hook show or pick
 * from the whole list (`AssetExclusionTable.tsx`, `AccountBillingInfo.tsx`), so
 * this walks every page (`useAuthenticatedFetchAllPages`) and holds `data`
 * back until the last one is in.
 *
 * When the delete of `AssetExclusionTable.tsx`
 * (`DELETE_ASSET_EXCLUSIONS_MUTATION_KEY`) settles, the cached pages are
 * dropped and the walk starts again, so a mounted list shows the delete. When
 * the last reader of the list unmounts, the cached pages are dropped too: a
 * reader mounted later walks the list again rather than collecting pages
 * cached before a create or update (`AssetExclusionInfo.tsx` sends those
 * through `useAuthenticatedMutationAsync`, which no mutation cache records).
 * Both follow `useQueryFeeStructures` above.
 */
export function useQueryAssetExclusions() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedAssetExclusionListItem>(
      ASSET_EXCLUSIONS_PATH,
    );

  // Restart the walk when the delete settles. See `useQueryFeeStructures` for
  // why this subscribes to the mutation cache's events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isAssetExclusionDelete(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [ASSET_EXCLUSIONS_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [ASSET_EXCLUSIONS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedAssetExclusionListItem[] } | undefined =
    useMemo(
      () => (hasEveryRow ? { data: rows } : undefined),
      [rows, hasEveryRow],
    );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

/**
 * The path of `getAllBillingReports`. Also the FIRST element of every
 * react-query key the page walk in `useQueryBillingReports` uses, so
 * `[BILLING_REPORTS_PATH]` reaches every page.
 */
export const BILLING_REPORTS_PATH = "/v1/billing/reports";

/**
 * The mutation key of the billing-report delete in `BillingReportsTable.tsx`.
 * `useQueryBillingReports` walks the list again when a mutation with this key
 * succeeds.
 */
export const DELETE_BILLING_REPORT_MUTATION_KEY = [
  "DELETE",
  BILLING_REPORTS_PATH,
] as const;

/** One item of `GET /v1/billing/reports`, as the route serialises it. */
export type SerializedBillingReportListItem = SerializedObject<
  UnpackResponse<BillingController["getAllBillingReports"]>
>["data"][number];

/**
 * The billing-report writes a mounted list shows: the delete of
 * `BillingReportsTable.tsx` (`DELETE_BILLING_REPORT_MUTATION_KEY`) and the
 * finalize of `FinalizeConfirmation.tsx`, whose key is
 * `["POST", "v1", "billing", "reports", reportId, "finalization"]`.
 */
const isBillingReportListWrite = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const key = mutation.options.mutationKey ?? [];
  const [method, path] = key;
  if (
    method === DELETE_BILLING_REPORT_MUTATION_KEY[0] &&
    path === DELETE_BILLING_REPORT_MUTATION_KEY[1]
  ) {
    return true;
  }
  return (
    key.length === 6 &&
    method === "POST" &&
    key[1] === "v1" &&
    key[2] === "billing" &&
    key[3] === "reports" &&
    key[5] === "finalization"
  );
};

/**
 * Every billing report of the firm. `GET /v1/billing/reports` answers a page
 * (APP-1651), and the one screen that reads this hook shows the whole list
 * (`BillingReportsTable.tsx`), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in.
 *
 * When a delete or a finalize (`isBillingReportListWrite`) settles, the cached
 * pages are dropped and the walk starts again, so a mounted list shows it.
 * When the last reader of the list unmounts, the cached pages are dropped too:
 * a reader mounted later walks the list again rather than collecting pages
 * cached before a run (`CreateBillingReport.tsx` sends that through
 * `useAuthenticatedMutationAsync`, which no mutation cache records) or before a
 * delete or finalize on `BillingReportInfo.tsx`. Both follow
 * `useQueryFeeStructures` above.
 */
export function useQueryBillingReports() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedBillingReportListItem>(
      BILLING_REPORTS_PATH,
    );

  // Restart the walk when a delete or finalize settles. See
  // `useQueryFeeStructures` for why this subscribes to the mutation cache's
  // events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isBillingReportListWrite(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [BILLING_REPORTS_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [BILLING_REPORTS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedBillingReportListItem[] } | undefined = useMemo(
    () => (hasEveryRow ? { data: rows } : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

export const feeStructureCycleHelpMessage =
  "For advanced billing, select the month whose end matches the end of the previous billing cycle. For arrears billing, select the month whose end matches the end of the current billing cycle.";

export const standardBillingDateHelpMessage = (
  <section>
    <p>
      Monthly fee structures, along with any fee structures with
      quarterly/annual frequencies whose billing cycle includes a month that
      matches this field, will apply fees in the new report
    </p>
    <p>
      If you run monthly or quarterly billing in advance, select the month that
      just completed to run billing for the period in advance. For example,
      select "June" if you are running billing for July (monthly) or Q3
      (quarterly).
    </p>
  </section>
);

export const periodPartitionHelpMessage =
  "Choose between billing based on the actual days in the billing period (e.g. 31/365 for March) versus a set period in the billing period (e.g. 1/12 for monthly billing, 1/4 for quarterly billing).";

export const BillingHelp = () => (
  <section>
    <p>Billing Reports are the account fee calculations for a given period.</p>
    <h3>Firm-Wide Billing</h3>
    <p>
      <strong>Firm-Wide Billing</strong> uses a standard billing cycle to
      calculate fees for <em>all</em> households and accounts with assigned fee
      structures. Select the month and year whose end date represents the end of
      a billing period for arrears billing, and the day before the start of an
      advance billing cycle. Be aware that AdviceCloud allows you to mix and
      match fee structures with different billing cycles in the same billing
      report. You may have fee structures that are on-cycle for the selected
      month, and other fee structures that are off-cycle for that month. The
      off-cycle fee structures will not accrue any fees for that billing report.
    </p>
    <p>
      For example, say an account has an assigned fee structure with a quarterly
      billing cycle, set to bill in advance for the months of January, April,
      July, and October. If the month of December is selected for the new
      billing report, then no fees will be accrued for that account in the
      generated billing report because it will only bill in January. If the
      month of January is selected, the report will include fees for that
      account for the upcoming months of February, March, and April.
    </p>
    <h3>One-Off Billing</h3>
    <p>
      <strong>One-Off Billing</strong> uses a custom dated billing cycle to
      calculate fees for <em>selected</em> households and accounts with assigned
      fee structures. Select the start and end dates of the billing period. For
      arrears billing, the balance on the end date will be used to determine the
      fee. For advance billing, the day before the start date will be used to
      determine the fee. Be aware that AdviceCloud allows you to mix and match
      fee structures with different billing cycles in the same billing report.
      You may have fee structures assigned that bill in advance <em>and</em>{" "}
      arrears among the selected households and accounts for the One-Off Billing
      Report.
    </p>
  </section>
);

export function feeLocationDisplay(
  feeLocation: "S" | "G" | "A",
  feeLocationAccountName: string = "Relocated",
) {
  return feeLocation === "A"
    ? feeLocationAccountName
    : feeLocation === "G"
      ? "Group"
      : "Self";
}

export function useQueryExportBillingReport(reportId: number) {
  return useAuthenticatedQuery<
    UnpackResponse<BillingController["exportBillingReport"]>,
    void,
    [string, string, number, string]
  >({
    queryKey: ["billing", "reports", reportId, "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}
